import React, { memo } from 'react';
import { Chip } from '@usepepper/sprout-kit-ui';
import { ColumnId } from '@sales-portal/domain/pricing/screens/management/types';
import { createStyles } from '@/contexts/theme-context';
import { PillFilterSection } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { Pressable, View } from 'react-native';
import { PricingFilterClearButton } from '@sales-portal/domain/pricing/components/pricing-filter-clear-button';
import { Typography } from '@usepepper/ui/atoms/typography';

type PillSectionData = Omit<PillFilterSection, 'getValues'>;

interface Props {
  onClear: () => void;
  onToggle: (value: string) => void;
  section: PillSectionData;
  selected: string[];
}

export const PricingFilterPillSection: React.FC<Props> = memo(
  ({ onClear, onToggle, section, selected }) => {
    const [styles] = useStyles();

    // Zero options with nothing selected: hide, per the panel's own
    // visibility check. An active selection (e.g. from a URL) still renders
    // the header so it stays clearable, just with no pills underneath.
    if (section.options.length === 0 && selected.length === 0) {
      return null;
    }

    const selectedSet = new Set(selected);
    const textTransform = section.id === ColumnId.UOM ? 'capitalize' : 'none';

    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Typography variant="label-md">{section.title}</Typography>
          {selected.length > 0 ? (
            <PricingFilterClearButton onPress={onClear} />
          ) : null}
        </View>
        <View style={styles.pillRow}>
          {section.options.map((option) => {
            const isSelected = selectedSet.has(option.value);

            return (
              <Pressable
                accessibilityLabel={option.label}
                accessibilityRole={section.singleSelect ? 'radio' : 'checkbox'}
                accessibilityState={{ checked: isSelected }}
                key={option.value}
                onPress={() => onToggle(option.value)}
              >
                <Chip circular pressable selected={isSelected} size="$md">
                  <Chip.Text textTransform={textTransform}>
                    {option.label}
                  </Chip.Text>
                </Chip>
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  container: {
    gap: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceLarge,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
